import React, { useEffect, useState } from 'react';
import { ArrowLeft, RefreshCcw } from 'lucide-react';
import Loader from '../../../components/Loader';
import { getAdminDashboard } from '../../../api/adminService';
import { AdminDashboard } from '../../../types/interfaces';
import DashboardView from '../../../components/Admin/DashboardView';
import InvestigationsView from '../../../components/Admin/InvestigationsView';
import ContactOrganizationsView from '../../../components/Admin/ContactOrganizationView';
import { useTranslation } from 'react-i18next';

/* ---------- tipos para navegación ---------- */
type View =
  | { name: 'dashboard' }
  | { name: 'investigations'; status: 'accepted'|'under_review'|'rejected'|'deleted_by_user' }
  | { name: 'contact_orgs' };

const Admin: React.FC = () => {
  const { t } = useTranslation();

  /* ---------- datos ---------- */
  const [data, setData] = useState<AdminDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setErr] = useState('');

  const fetchData = () => {
    setLoading(true);
    setErr('');
    getAdminDashboard()
      .then((r) => setData(r.data))
      .catch(() => setErr(t('errors.admin_data_load')))
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [t]);

  /* ---------- navegación ---------- */
  const [view, setView] = useState<View>({ name: 'dashboard' });
  const [history, setHis] = useState<View[]>([]);

  const goTo = (next: View) => {
    setHis((h) => [...h, view]);
    setView(next);
  };

  const goBack = () =>
    setHis((h) => {
      const prev = h[h.length - 1];
      if (prev) setView(prev);
      return h.slice(0, -1);
    });

  /* ---------- guards ---------- */
  if (loading) return <Loader />;
  if (error)   return <p className="text-red-500">{error}</p>;
  if (!data)   return null;

  /* ---------- UI ---------- */
  return (
    <div className="space-y-8">
      {/* Barra superior */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {history.length > 0 && (
            <button onClick={goBack} className="p-2 rounded-md bg-gray-100 hover:bg-gray-200">
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <h1 className="text-2xl font-bold">
            {view.name === 'dashboard'
              ? t('admin.title')
              : view.name === 'investigations'
              ? t('admin.investigations')
              : t('admin.contact_orgs')}
          </h1>
        </div>

        {view.name === 'dashboard' && (
          <>
            {/* recarga solo los datos, no la página completa */}
            <button
              onClick={fetchData}
              className="flex items-center gap-2 text-sm px-4 py-2 bg-gray-100 border border-gray-300 rounded-md shadow hover:bg-gray-200 transition"
            >
              <RefreshCcw className="h-4 w-4" />
              {t('admin.reload')}
            </button>
          </>
        )}
      </div>

      {/* Vistas */}
      {view.name === 'dashboard' && (
        <DashboardView
          data={data}
          onSelectInvestigationStatus={(s) => goTo({ name: 'investigations', status: s })}
          onOpenContactOrgs={() => goTo({ name: 'contact_orgs' })}
        />
      )}

      {view.name === 'investigations' && (
        <InvestigationsView
          status={view.status}
          onSelectStatus={(s) => setView({ name: 'investigations', status: s })}
        />
      )}

      {view.name === 'contact_orgs' && <ContactOrganizationsView />}
    </div>
  );
};

export default Admin;
